import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { useJuneTrackCall } from "../../../utils/june/analytics";

import { taskPopupState } from "../../../recoil/taskDetail/taskPopupState";
import { LinkAddDialog } from "../../../compoenets/organism/TaskDetail/Link/LinkAddDialog";
import PropertyTitle from "../Common/PropertyTitle";
import { AddButton } from "./AddButton";
import LinkIcon from "../../../compoenets/atom/icons/LinkIcon";
import { BlockLinkType } from "../../../types/block/type";
import { LinkItem } from "./LinkItem";
import { LinkClientData } from "./type";

import LinkEmptyButton from "./LinkEmptyButton";

interface LinkModalProps {
  expand: boolean;
  linkMoreModalRef: React.RefObject<HTMLDivElement>;
}

export default function LinkModal({ expand, linkMoreModalRef }: LinkModalProps) {
  const [taskDetail, setTaskDetail] = useRecoilState(taskPopupState);
  const [visibleTooltipIndex, setVisibleTooltipIndex] = useState<number | null>(null);
  const [isResourceAddModalOn, setIsResourceAddModalOn] = useState(false);
  const [isSidebarModalOn, setIsSidebarModalOn] = useState(-1);
  const [selectedItem, setSelectedItem] = useState<LinkClientData | null>(null);

  const { data } = taskDetail;
  const trackCall = useJuneTrackCall();

  const handleIconEnter = (index: number) => (e: React.MouseEvent<HTMLDivElement>) => {
    setVisibleTooltipIndex(index);
  };

  const handleIconLeave = () => {
    setVisibleTooltipIndex(null);
  };

  const handleDeleteLink = (index: number) => {
    setTaskDetail((prev) => {
      if (!prev?.data?.linkData) return prev;

      return {
        ...prev,
        data: {
          ...prev.data,
          linkData: prev.data.linkData.filter((_, i) => i !== index),
        },
      };
    });
    setIsSidebarModalOn(-1);
  };

  const handleResourceAdd = () => {
    setIsResourceAddModalOn(true);
  };

  const handleMove = (url: string | undefined) => () => {
    if (!url) return;

    trackCall("click_link", { location: expand ? "full" : "modal" });
    if (url.startsWith("http://") || url.startsWith("https://")) {
      window.open(url, "_blank");
    } else {
      window.open("https://" + url, "_blank");
    }
  };

  const handleAddLink = ({ url, pageName }: { url: string; pageName: string }) => {
    setTaskDetail((prev) => {
      if (!prev?.data) return prev;

      const newLink: BlockLinkType = {
        url,
        name: pageName,
      };

      return {
        ...prev,
        data: {
          ...prev.data,
          linkData: [...(prev.data.linkData ?? []), newLink],
        },
      };
    });
  };

  const handleEditLink = (index: number) => {
    setSelectedItem({
      index,
      url: taskDetail.data?.linkData?.[index]?.url ?? "",
      name: taskDetail.data?.linkData?.[index]?.name ?? "",
    });
    setIsSidebarModalOn(-1);
  };

  const handleSaveEdit = ({
    index,
    url,
    pageName,
  }: {
    index: number;
    url: string;
    pageName: string;
  }) => {
    setTaskDetail((prev) => {
      if (!prev?.data?.linkData) return prev;

      const updatedLinkData = prev.data.linkData.map((link, i) =>
        i === index ? { ...link, url, name: pageName } : link
      );

      return {
        ...prev,
        data: {
          ...prev.data,
          linkData: updatedLinkData,
        },
      };
    });
    setIsResourceAddModalOn(false);
    setSelectedItem(null);
  };

  const handleModalClose = (open: boolean) => {
    setIsResourceAddModalOn(open);
    if (!open) {
      setSelectedItem(null);
    }
  };

  const handleMoreClick = (index: number) => {
    if (isSidebarModalOn === index) {
      setIsSidebarModalOn(-1);
    } else {
      setIsSidebarModalOn(index);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (linkMoreModalRef.current && !linkMoreModalRef.current.contains(event.target as Node)) {
        setIsSidebarModalOn(-1);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      // 컨텍스트 메뉴가 열려있을 때 ESC 키 이벤트 가로채기
      if (event.key === "Escape" && isSidebarModalOn !== -1) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        setIsSidebarModalOn(-1);
        return;
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown, true); // 캡처 단계에서 가로채기

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isSidebarModalOn]);

  useEffect(() => {
    if (selectedItem) {
      setIsResourceAddModalOn(true);
    }
  }, [selectedItem]);

  return (
    <>
      <div className="flex h-max min-h-[30px] w-full flex-row content-center justify-center text-[14px] font-normal leading-[150%]">
        <PropertyTitle
          Icon={<LinkIcon />}
          label="Links"
          expand={expand}
        />

        {data && data.linkData != null && data.linkData.length > 0 ? (
          <div className="flex w-full flex-1 flex-wrap items-center gap-2 text-placeholder">
            {data.linkData &&
              data.linkData.map((item, index) => (
                <LinkItem
                  key={index}
                  url={item.url}
                  name={item.name}
                  index={index}
                  visibleTooltipIndex={visibleTooltipIndex}
                  isSidebarModalOn={isSidebarModalOn}
                  linkMoreModalRef={linkMoreModalRef}
                  onMouseEnter={handleIconEnter}
                  onMouseLeave={handleIconLeave}
                  onMove={handleMove}
                  onEdit={handleEditLink}
                  onDelete={handleDeleteLink}
                  onMoreClick={handleMoreClick}
                />
              ))}
            <AddButton
              onClick={handleResourceAdd}
              openMore={false}
            />
          </div>
        ) : (
          <LinkEmptyButton handleResourceAdd={handleResourceAdd} />
        )}
      </div>

      <LinkAddDialog
        open={isResourceAddModalOn}
        onOpenChange={handleModalClose}
        initData={selectedItem}
        onAdd={handleAddLink}
        onEdit={handleSaveEdit}
      />
    </>
  );
}
